import { PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";
import type { PayrollEditorQueries } from "@/payroll/editor/query";
import { editorError } from "@/payroll/editor/state";
import type { EditorUpload } from "./use-editor-upload";

const STATUS_DOT = {
  ready: "bg-stroke-interactive",
  pending: "bg-stroke-secondary",
  extraction_failed: "bg-destructive",
} as const;

/**
 * Attached files as dismissible chips inside the composer, directly under the
 * attach button that added them. A file is context the agent will read, so it
 * belongs next to the message it arrives with rather than in a panel of its own.
 */
export const EditorAttachments = ({
  documents,
  remove,
  upload,
  disabled,
  canRemove,
}: {
  documents: PayrollEditorQueries["documents"];
  remove: PayrollEditorQueries["remove"];
  upload: EditorUpload;
  /** Sending is held back, which is not the same as the file being stuck. */
  disabled: boolean;
  /** Whether this proposal can be changed at all. */
  canRemove: boolean;
}) => {
  const { pending, localError } = upload;
  const files = documents.data ?? [];
  const busy = upload.isUploading || remove.isPending;
  const notice =
    localError ||
    (pending && !upload.isUploading && editorError(upload.error)) ||
    (!pending && upload.hasReceipt
      ? "An upload was interrupted. Choose the same file to finish it."
      : "") ||
    (remove.error || documents.error
      ? editorError(remove.error || documents.error)
      : "");
  if (!files.length && !pending && !notice) return null;
  return (
    <Stack
      gap="none"
      align="stretch"
      direction="column"
      className="flex flex-col gap-2 px-3 pb-3"
    >
      {(!!files.length || !!pending) && (
        <ul className="flex flex-wrap gap-1.5">
          {files.map((file) => (
            <li
              key={file.id}
              className="flex max-w-full items-center gap-1.5 rounded-full border border-stroke-tertiary bg-surface-secondary py-1 pl-2.5 pr-1 text-sm"
            >
              <span
                aria-hidden
                className={cn(
                  "h-1.5 w-1.5 shrink-0 rounded-full",
                  STATUS_DOT[file.status],
                )}
              />
              <span className="truncate" title={file.title}>
                {file.title}
              </span>
              {file.status !== "ready" && (
                <span
                  role={
                    file.status === "extraction_failed" ? "alert" : "status"
                  }
                >
                  {file.statusLabel}
                </span>
              )}
              <Button
                size="iconSm"
                variant="ghost"
                className="shrink-0"
                // Not the send gate. A file whose text could not be read is
                // exactly what holds sending back, and the API will not accept
                // a message until it is gone, so disabling Remove for the same
                // reason left the owner with a composer they could not use and
                // a file they could not take out of it.
                disabled={!canRemove || busy || !!pending}
                aria-label={`Remove ${file.title}`}
                onClick={() => {
                  if (
                    window.confirm(`Remove ${file.title} from this proposal?`)
                  )
                    remove.mutate(file.id);
                }}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
          {!!pending && upload.isUploading && (
            <li
              className="flex items-center gap-1.5 rounded-full border border-stroke-tertiary bg-surface-secondary py-1 pl-2.5 pr-3 text-sm text-secondary-text"
              role="status"
            >
              <PaperClipIcon className="h-3 w-3 shrink-0" />
              <span className="truncate">Adding {pending.file.name}…</span>
            </li>
          )}
        </ul>
      )}
      {!!notice && (
        <Stack
          gap="none"
          align="stretch"
          direction="row"
          role="alert"
          className="flex flex-wrap items-center gap-2 text-sm"
        >
          <span>{notice}</span>
          {!!pending && !upload.isUploading && (
            <Button
              size="xs"
              variant="tertiary"
              disabled={disabled}
              onClick={() => void upload.upload(pending)}
            >
              Retry
            </Button>
          )}
          {/* Whenever a receipt is still held and nothing is uploading. It
              used to appear only for a 400 or a 422, so an upload cut off by a
              closed tab left a receipt that refused every other file and
              offered no way to let go of it.

              The list is refreshed first: the upload may well have landed
              before the connection went, and the file appearing is a better
              answer than sending it a second time. */}
          {upload.hasReceipt && !upload.isUploading && (
            <Button
              size="xs"
              variant="text"
              onClick={async () => {
                await documents.refetch();
                upload.chooseAnotherFile();
              }}
            >
              Choose another file
            </Button>
          )}
          {!!(remove.error || documents.error) && (
            <Button
              size="xs"
              variant="tertiary"
              onClick={() => {
                remove.reset();
                void documents.refetch();
              }}
            >
              Refresh files
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
};
